import React, { useEffect, useRef, useState } from "react";
import { Alert, Button, Paper, Stack, TablePagination, Box } from "@mui/material";
import { DeleteForever, DynamicFeed, Deselect } from "@mui/icons-material";
import { enqueueSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import LabelIcon from '@mui/icons-material/Label';

import QuerySearchBar, { defaultQuerySearchBarSettings, QuerySearchBarSettings } from "./QuerySearchBar";
import TagList from "routes/tagManagement/TagList";
import { Query } from "system/query/Query";
import { ApiPagination, Tag } from "routes/tagManagement/TagManagementPage";
import APIRequest, { APIError, enqueueAPIErrorSnackbar } from "system/APIRequest";
import { MatchFieldValue } from "./MatchFieldsButton";
import { ResourceTagEntry } from "routes/tagManagement/TagQueriesDialog";
import SavedQueryRow from "components/queries/savedQueries/SavedQueryRow";
import ConfirmationDialog from "components/dialogs/ConfirmationDialog";
import BulkEditTagsDialog from "./BulkEditTagsDialog";

interface QueryListProps {
  canEditQuery?: boolean;
  enableMultiEditing?: boolean;
  multipleSelect?: boolean;
  onQueriesLoaded?(queries: Array<Query>): void;
  onReload?(): void;
  onSelectQueries?(queries: Array<Query>): void;
  persistSelection?: boolean;
  scrollRef?: React.RefObject<HTMLElement>;
  selectedQueries?: Array<Query>;
  showCopyButton?: boolean;
  showDeleteButton?: boolean;
  showLoadButton?: boolean;
  showSelectMultipleToggle?: boolean;
  showShareButton?: boolean;
  reloadCounter?: number;
}

export interface LoadQueriesOptions {
  datasetId?: string;
  includeShared?: boolean;
  matchField?: MatchFieldValue;
  pagination?: {
    page: number,
    per_page: number
  };
  search?: string;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
  tags?: Array<Tag>;
}

export async function loadQueries(options: LoadQueriesOptions): Promise<any> {
  let queryIds: Array<string> = [];
  if (options.tags && options.tags.length > 0) {
    // We are loading queries based on a selection of one or more tags.  The first step of this is
    // to take the selected tags and look up their ResourceTagEntry.  Each ResourceTagEntry will 
    // link to a query id.  We can then use that query id to look up queries for the selected tag(s).
    const qParts = [{
      id: "resource_type",
      op: "=",
      value: "query"
    }];

    const requests: Array<Promise<Array<ResourceTagEntry>>> = options.tags.map((tag) => {
      return new Promise<Array<ResourceTagEntry>>(async (resolve, reject) => {
        const queryParams = {
          q: JSON.stringify(qParts),
          resource_tag_id: tag.id,
          per_page: tag.query_count+""
        }
        const response: any = await APIRequest("obj/resource-tag-entry", queryParams, undefined, true);
        resolve(response?.output || []);
      });
    });

    const responses = await Promise.all(requests);

    let matchedQueryIds: Array<string> = [];
    for (let i=0; i<responses.length; i++) {
      // Now that we have one or more ResourceTagEntry records we can filter for eligible queries.
      // For a query to be eligible it must be tagged with ALL of the selected tags - tag filtering
      // is an AND operation.
      const currentResourceTagEntry = responses[i];
      if (i == 0) {
        // Start off with all of the queries for the first ResourceTagEntry.  This will give us an
        // initial list of query ids.
        const tagQueryIds = currentResourceTagEntry.map((entry) => entry.resource_id);
        matchedQueryIds = tagQueryIds;
        continue;
      }
      // Since we are AND'ing the selected tags we can only keep the query ids that are referenced
      // across ALL ResourceTagEntries.
      matchedQueryIds = matchedQueryIds.filter((id) => currentResourceTagEntry.find((entry) => entry.resource_id == id));
    }

    queryIds = matchedQueryIds;

    if (queryIds.length === 0) {
      // There are no queries that match ALL of the selected tags.
      return [];
    }
  }

  // Once we have a list of query ids to get, simply build a request and get the queries.
  const queryFilters = [{
    id: "is_ephemeral",
    op: "=",
    value: "0"
  }];

  if (options.datasetId) {
    queryFilters.push({
      id: "dataset_id",
      op: "contains",
      value: options.datasetId
    });
  }

  const queryParams: any = {
    include_shared: options.includeShared || false,
    page: options.pagination?.page || 1,
    per_page: options.pagination?.per_page || 20,
    q: JSON.stringify(queryFilters)
  };
  if (options.sortBy) {
    queryParams.sort = options.sortBy;
  }
  if (options.sortOrder) {
    queryParams.sort_order = options.sortOrder;
  }
  if (options.search) {
    if (options.matchField === "any") {
      queryParams.search = options.search;
    } else {
      queryParams.q = JSON.stringify([{
        id: options.matchField,
        op: "contains",
        value: options.search
      }]);
    }
  }
  let url = "obj/query";
  if (queryIds.length > 0) {
    url += "/" + queryIds.join(",");
  }

  let response: any = await APIRequest(url, queryParams, undefined, true);
  return response;
}

export async function loadTags(): Promise<any> {
  const queryParams = {
    per_page: "20",
    q: JSON.stringify([{
      id: "query_count",
      op: ">",
      value: "0"
    }]),
    sort: "use_count",
    sort_order: "desc"
  };
  const response: any = await APIRequest("obj/resource-tag", queryParams, undefined, true);
  return response;
}

const PaginationStart: ApiPagination = {
  cursor: "",
  page: 1,
  per_page: 10,
  total: 0
};

export default function QueryList(props: QueryListProps): React.JSX.Element {
  const { i18n } = useTranslation();

  const [searchBarSettings, setSearchBarSettings] = useState<QuerySearchBarSettings>(defaultQuerySearchBarSettings);
  const [selectMultiple, setSelectMultiple] = useState<boolean>(false);

  const [queries, _setQueries] = useState<Array<Query>>([]);
  const [_selectedQueries, _setSelectedQueries] = useState<Array<Query>>([]);
  const [queriesToDelete, setQueriesToDelete] = useState<Array<Query>>([]);
  const [pagination, setPagination] = useState<ApiPagination>({...PaginationStart});
  const loadQueriesTimer = useRef<NodeJS.Timeout | null>(null);

  const [tags, setTags] = useState<Array<Tag>>([]);
  const [selectedTags, setSelectedTags] = useState<Array<Tag>>([]);
  const loadTagsTimer = useRef<NodeJS.Timeout | null>(null);

  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState<boolean>(false);
  const [showBulkEditTagsDialog, setShowBulkEditTagsDialog] = useState<boolean>(false);

  let clickAction = (props.canEditQuery && !selectMultiple) ? "edit" : "select";
  const selectedQueries = props.selectedQueries || _selectedQueries;

  function setQueries(value: Array<Query>): void {
    _setQueries(value);
    if (props.onQueriesLoaded) {
      props.onQueriesLoaded(value);
    }
  }

  function setSelectedQueries(value: Array<Query>): void {
    if (!props.selectedQueries) {
      _setSelectedQueries(value);
    }
    if (props.onSelectQueries) {
      props.onSelectQueries(value);
    }
  }

  function loadQueriesWrapper(searchBarSettings: QuerySearchBarSettings, selectedTags?: Array<Tag>, pagination?: ApiPagination): Promise<{queries: Array<Query>, pagination: ApiPagination}> {
    if (loadQueriesTimer.current) {
      clearTimeout(loadQueriesTimer.current);
    }

    let options: LoadQueriesOptions = {
      includeShared: searchBarSettings.includeShared,
      matchField: searchBarSettings.matchField,
      search: searchBarSettings.searchValue,
      sortBy: searchBarSettings.sortBy,
      sortOrder: searchBarSettings.sortOrder,
    };
    if (selectedTags?.length) {
      options.tags = selectedTags;
    }
    if (pagination) {
      options.pagination = pagination;
    }

    const promise = new Promise<{queries: Array<Query>, pagination: ApiPagination}>((resolve, reject) => {
      loadQueriesTimer.current = setTimeout(async () => {
        const response = await loadQueries(options);
        let queries: Array<Query> = [];
        if (response.output) {
          queries = Array.isArray(response.output) ? response.output : [response.output];
        }
        const responsePagination: ApiPagination = response.pagination
          ? response.pagination
          : {...PaginationStart, per_page: pagination?.per_page || PaginationStart.per_page};
        resolve({ queries, pagination: responsePagination });
      }, 300);
    });

    return promise;
  }

  function loadTagsWrapper(): Promise<{tags: Array<Tag>, pagination: ApiPagination}> {
    if (loadTagsTimer.current) {
      clearTimeout(loadTagsTimer.current);
    }

    const promise = new Promise<{tags: Array<Tag>, pagination: ApiPagination}>((resolve, reject) => {
      loadTagsTimer.current = setTimeout(async () => {
        const response = await loadTags();
        const tags = response.output || [];
        const pagination: ApiPagination = response.pagination;
        resolve({ tags, pagination });
      }, 300);
    });

    return promise;
  }

  async function load(): Promise<void> {
    const tags = await loadTagsWrapper();

    const currentSelectedTags = tags.tags.filter((tag) => selectedTags.find((sTag) => sTag.id === tag.id));

    const queries = await loadQueriesWrapper(searchBarSettings, currentSelectedTags, pagination);

    setSelectedTags(currentSelectedTags);
    setTags(tags.tags);
    setQueries(queries.queries);
    setPagination(queries.pagination);
  }

  async function handleClickTag(tag: Tag): Promise<void> {
    const isSelected = !!selectedTags.find((t) => t.id === tag.id);
    const newPagination = {
      ...pagination,
      page: 1,
      per_page: pagination.per_page
    };

    let newSelectedTags: Array<Tag> = [];
    if (isSelected) {
      newSelectedTags = selectedTags.filter((selectedTag) => selectedTag.id !== tag.id);
    } else {
      newSelectedTags = [...selectedTags, tag];
    }

    setSelectedTags(newSelectedTags);
    const loadQueriesResponse = await loadQueriesWrapper(searchBarSettings, newSelectedTags, newPagination);
    setQueries(loadQueriesResponse.queries);
    setPagination(loadQueriesResponse.pagination);
  }

  async function handleChangeSearchSettings(settings: QuerySearchBarSettings) {
    const newPagination = {
      ...pagination,
      page: 1
    };
    setSearchBarSettings(settings);
    const loadQueriesResponse = await loadQueriesWrapper(settings, selectedTags, newPagination);
    setQueries(loadQueriesResponse.queries);
    setPagination(loadQueriesResponse.pagination);
  }

  function handleChangeSelectMultiple(checked: boolean): void {
    setSelectMultiple(checked);
    if (!checked) {
      setSelectedQueries([]);
    }
  }

  function handleClickQuery(query: Query): void {
    if (clickAction === "edit") {
      // Edit query
      return;
    }

    let newSelection = [];
    if (selectedQueries && selectedQueries?.find((q) => q.id === query.id)) {
      newSelection = selectedQueries.filter((q => q.id !== query.id));
    } else if (props.multipleSelect || selectMultiple) {
      newSelection = [...(selectedQueries || []), query];
    } else {
      newSelection = [query];
    }
    setSelectedQueries(newSelection);
  }

  async function handleDeleteSelectedQueries(queriesToDelete: Array<Query>): Promise<void> {
    const deleteRequests = queriesToDelete.map((selectedQuery) => {
      if (!selectedQuery.id){
        return Promise.resolve(true);
      }
      return Query.delete(selectedQuery.id);
    });
    try {
      await Promise.all(deleteRequests);
    } catch (error) {
      enqueueAPIErrorSnackbar(error as APIError, enqueueSnackbar);
    } finally {
      setSelectedQueries([]);
      let newPage = pagination.page;
      const pageCount = Math.ceil((pagination.total-queriesToDelete.length) / pagination.per_page);
      if (newPage > pageCount) {
        newPage = pageCount;
      }
      const newPagination: ApiPagination = {
        ...pagination,
        page: newPage
      }
      const loadQueriesResponse = await loadQueriesWrapper(searchBarSettings, selectedTags, newPagination);
      setQueries(loadQueriesResponse.queries);
      setPagination(loadQueriesResponse.pagination);
    }
  }

  async function handlePageChanged(page: number): Promise<void> {
    scrollToTop();
    const newPagination = {
      ...pagination,
      page: page
    };
    const loadQueriesResponse = await loadQueriesWrapper(searchBarSettings, selectedTags, newPagination);
    setQueries(loadQueriesResponse.queries);
    if (!props.persistSelection) {
      setSelectedQueries([]);
    }
    setPagination(loadQueriesResponse.pagination);
  }

  async function handleRowsPerPageChange(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): Promise<void> {
    const newPagination = {
      ...pagination,
      page: 1,
      per_page: parseInt(event.target.value, 10)
    };
    const loadQueriesResponse = await loadQueriesWrapper(searchBarSettings, selectedTags, newPagination);
    setQueries(loadQueriesResponse.queries);
    if (!props.persistSelection) {
      setSelectedQueries([]);
    }
    setPagination(loadQueriesResponse.pagination);
  }

  function hasFiltersSet(): boolean {
    if (selectedTags.length > 0
      || searchBarSettings.includeShared
      || searchBarSettings.searchValue) {
      return true;
    }
    return false;
  }

  function scrollToTop(): void {
    if (props.scrollRef?.current) {
      // If this component is being used in a Dialog, the scrollRef will be the constrained element
      // that needs to scroll to the top.
      props.scrollRef.current.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      // If this component is being used as part of the page, scroll to the top.
      scrollTo({ top: 0, behavior: "smooth" });
    }
  }

  useEffect(() => {
    load();
  }, [props.reloadCounter]);

  function handleSelectAll(queries: Query[]): void {
    setSelectedQueries(queries);
  }

  function handleDeselectAll(): void {
    setSelectedQueries([]);
  }

  const isMultiEditing = !!props.enableMultiEditing && selectMultiple;

  const canEditQuery = props.canEditQuery && !selectMultiple ? true : false;

  const deleteConfirmationDescription = queriesToDelete.length === 1
    ? i18n.t("query-list.delete-confirmation.description-single", {name: queriesToDelete[0].name})
    : i18n.t("query-list.delete-confirmation.description-multiple", {count: queriesToDelete.length})

  return (
    <Stack direction="row">
      {tags.length > 0 &&
        <TagList
          onClick={handleClickTag}
          selectedTags={selectedTags}
          tags={tags}
        />
      }
      <Stack
        sx={{
          gap: 1,
          flex: 1
        }}>
        <Paper sx={{ position: "sticky", top: "0px", zIndex: 1200 }}>
          <Stack
            sx={{
              gap: 1,
              p: 1
            }}>
            <QuerySearchBar
              onChangeSearchSettings={handleChangeSearchSettings}
              onChangeSelectMultiple={handleChangeSelectMultiple}
              settings={searchBarSettings}
              showSelectMultipleToggle={props.showSelectMultipleToggle}
            />
            {isMultiEditing &&
              <Stack direction="row" sx={{alignItems: "center", flex: "wrap", gap: 1}}>
                <Button
                  color="primary"
                  onClick={() => handleSelectAll(queries)}
                  size="small"
                  startIcon={<DynamicFeed/>}
                  variant="outlined"
                >
                  {i18n.t("query-management-page.select-all")}
                </Button>
                {selectedQueries.length > 0 && (
                  <Stack direction="row" sx={{alignItems: "center", flex: "wrap", gap: 1}}>
                    <Button
                      color="primary"
                      onClick={() => handleDeselectAll()}
                      size="small"
                      startIcon={<Deselect/>}
                      variant="outlined"
                    >
                      {i18n.t("query-management-page.deselect-all")}
                    </Button>
                    <Button
                      color="primary"
                      onClick={() => setShowBulkEditTagsDialog(true)}
                      size="small"
                      startIcon={<LabelIcon/>}
                      variant="outlined"
                    >
                      {i18n.t("query-list.bulk-edit-tags")}
                    </Button>
                    <Button
                      color="error"
                      onClick={() => {
                        setQueriesToDelete(selectedQueries);
                        setShowDeleteConfirmation(true);
                      }}
                      size="small"
                      startIcon={<DeleteForever/>}
                      variant="outlined">
                        {i18n.t("query-list.delete-selected")}
                    </Button>
                  </Stack>
                )}
              </Stack>
            }
          </Stack>
        </Paper>
        {queries.map((query): React.JSX.Element => {
          const isSelected = selectedQueries.some((selectedQuery) => query.id === selectedQuery.id);
          return (
            <SavedQueryRow
              key={query.id}
              buttonSize="small"
              canDelete={true}
              canEdit={canEditQuery}
              isSelected={isSelected}
              loadOwnQueryInfo={true}
              onChange={async (queryId) => {
                const loadQueriesResponse = await loadQueriesWrapper(searchBarSettings, selectedTags, pagination);
                setQueries(loadQueriesResponse.queries);
                setPagination(loadQueriesResponse.pagination);
              }}
              onClick={() => {
                handleClickQuery(query);
              }}
              onCopyCreated={async (queryId) => {
                const loadQueriesResponse = await loadQueriesWrapper(searchBarSettings, selectedTags, pagination);
                setQueries(loadQueriesResponse.queries);
                setPagination(loadQueriesResponse.pagination);
              }}
              onDelete={() => {
                setQueriesToDelete([query]);
                setShowDeleteConfirmation(true);
              }}
              savedQuery={query}
              showCopyButton={props.showCopyButton}
              showDeleteButton={props.showDeleteButton}
              showLoadQueryButton={props.showLoadButton}
              showShareButton={props.showShareButton}
            />
          )
        })}
        {queries.length === 0 &&
          <Paper sx={{p: 1}}>
            <Alert severity="info" variant="outlined">
              {hasFiltersSet() && i18n.t("query-list.no-matches") || i18n.t("query-list.no-queries")}
            </Alert>
          </Paper>
        }
        <Stack
          sx={{
            alignItems: "end",
            padding: 1
          }}>
          <TablePagination
            component="div"
            count={pagination.total}
            onPageChange={(event, page) => handlePageChanged(page+1)}
            onRowsPerPageChange={(event) => handleRowsPerPageChange(event)}
            rowsPerPage={pagination.per_page || 10}
            page={pagination.page-1}
          />
        </Stack>
      </Stack>
      {showDeleteConfirmation &&
        <ConfirmationDialog
          isOpen={showDeleteConfirmation}
          title={i18n.t(`query-list.delete-confirmation.title-${queriesToDelete.length === 1 ? "single" : "multiple"}`)}
          description={deleteConfirmationDescription}
          onClose={async (isConfirmed:boolean) => {
            if (isConfirmed && queriesToDelete.length > 0) {
              handleDeleteSelectedQueries(queriesToDelete);
            }
            setShowDeleteConfirmation(false);
          }}
        />
      }
      {showBulkEditTagsDialog &&
        <BulkEditTagsDialog
          onChange={() => {
            setShowBulkEditTagsDialog(false);
            if (props.onReload) {
              props.onReload();
            }
          }}
          onClose={() => {
            setShowBulkEditTagsDialog(false);
          }}
          queries={selectedQueries}
        />
      }
    </Stack>
  );
}
